/**
 * Shared helpers for the map estimator: how surfaces are measured, soiling
 * multipliers, and how a drawn shape becomes a quote line.
 */

export const MEASURES = [
  { value: 'area', label: 'Area (m²)', unit: 'm2', shape: 'polygon', hint: 'Outline the surface — driveways, paths, patios, pool surrounds, decks' },
  { value: 'ring', label: 'Outer − inner outline (m²)', unit: 'm2', shape: 'polygon', dim: 'Width', hint: 'Draw the outer edge, then the inner edge — the band between is the area (eaves). Until the inner edge is drawn, outer edge × width is the estimate.' },
  { value: 'length', label: 'Length (lineal m)', unit: 'lm', shape: 'line', hint: 'Trace the run — gutters' },
  { value: 'length_height', label: 'Length × height (m²)', unit: 'm2', shape: 'line', dim: 'Height', hint: 'Trace the run and set a height — walls, fences, retaining walls' },
  { value: 'length_width', label: 'Length × width (m²)', unit: 'm2', shape: 'line', dim: 'Width', hint: 'Trace the run and set a width — eaves by the metre, paths of a known width' },
  { value: 'length_faces', label: 'Length × (side + bottom) (m²)', unit: 'm2', shape: 'line', dim: 'Side', dim2: 'Bottom', hint: 'Trace the run; the side and bottom faces add up to the width — gutters' }
];
export const measureInfo = (value) => MEASURES.find((m) => m.value === value) || MEASURES[0];
/** Measures that take a second dimension (height / width / side) from the shape. */
export const usesDim = (measure) => measure === 'length_height' || measure === 'length_width' || measure === 'ring' || measure === 'length_faces';
/** Measures with a third figure (gutters: the bottom face). */
export const usesDim2 = (measure) => measure === 'length_faces';
/** Measures whose area is length × the dimension(s). */
export const isLengthTimes = (measure) => measure === 'length_height' || measure === 'length_width' || measure === 'length_faces';
export const dimLabel = (measure) => measureInfo(measure).dim || 'Height';
export const dimLabel2 = (measure) => measureInfo(measure).dim2 || 'Bottom';
export const DIM_DEFAULT = { length_height: 2.7, length_width: 0.6, ring: 0.6, length_faces: 0.1 };

export const SOILING = [
  { value: 'light', label: 'Light', key: 'soiling_light', fallback: 1 },
  { value: 'moderate', label: 'Moderate', key: 'soiling_moderate', fallback: 1.25 },
  { value: 'heavy', label: 'Heavy', key: 'soiling_heavy', fallback: 1.5 }
];

/**
 * Painting (backlog #772): a surface's trade is wash | paint. A painting area is
 * priced rate (per coat) × coats × condition — the condition is the prep a tired
 * surface needs, and rides in the shape's `soiling` field the way soiling does
 * for washing. `coats` is set only on painting areas.
 */
export const TRADES = [
  { value: 'wash', label: 'Washing' },
  { value: 'paint', label: 'Painting' }
];
export const tradeOf = (surface) => (surface?.trade === 'paint' ? 'paint' : 'wash');
export const CONDITION = [
  { value: 'good', label: 'Good', key: 'paint_condition_good', fallback: 1 },
  { value: 'fair', label: 'Fair', key: 'paint_condition_fair', fallback: 1.25 },
  { value: 'poor', label: 'Poor', key: 'paint_condition_poor', fallback: 1.5 }
];
export const COATS = [1, 2, 3, 4];
/** The Soiling options for washing, the Condition options for painting. */
export const factorOptions = (trade) => (trade === 'paint' ? CONDITION : SOILING);
export const defaultFactor = (trade) => (trade === 'paint' ? 'good' : 'light');
export function defaultCoats(settings = {}) {
  const n = parseInt(settings.paint_default_coats, 10);
  return COATS.includes(n) ? n : 2;
}
const coatsText = (n) => `${n} coat${n === 1 ? '' : 's'}`;
/** ", heavy soiling" / ", fair condition" — nothing for the plain light / good. */
function factorText(soiling, coats) {
  if (!soiling || soiling === 'light' || soiling === 'good') return '';
  return coats ? `, ${soiling} condition` : `, ${soiling} soiling`;
}

/** { light: 1, moderate: 1.25, heavy: 1.5, good: 1, fair: 1.25, poor: 1.5 } from the settings map. */
export function soilingFactors(settings = {}) {
  return Object.fromEntries([...SOILING, ...CONDITION].map((s) => {
    const n = Number(settings[s.key]);
    return [s.value, Number.isFinite(n) && n > 0 ? n : s.fallback];
  }));
}

export const round1 = (n) => Math.round((Number(n) + Number.EPSILON) * 10) / 10;
export const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
/** Heights / widths print to 2 decimals when needed: a 0.15 m gutter face must not read 0.2. */
export const fmtDim = (n) => round2(n).toLocaleString('en-AU', { maximumFractionDigits: 2 });

export function unitLabel(unit) {
  return unit === 'm2' ? 'm²' : unit === 'lm' ? 'lm' : '';
}
export function fmtMeasure(value, unit) {
  const n = round1(value);
  return `${n.toLocaleString('en-AU', { maximumFractionDigits: 1 })} ${unitLabel(unit)}`.trim();
}

/**
 * Price one drawn shape.
 * @param shape   { label, measure, raw_value (m² or lineal m), height_m, soiling, coats (painting) }
 * @param surface { name, rate }            (may be null when the surface was deleted)
 * @param factors soilingFactors(settings)
 */
export function priceShape(shape, surface, factors) {
  const coats = Number(shape.coats) >= 1 ? Math.round(Number(shape.coats)) : null;
  const info = measureInfo(shape.measure);
  // height_m holds the second dimension: a height for walls/fences, a width for
  // eaves. For a ring the raw value is already the band area (or the
  // perimeter × width estimate), so the dimension is recorded, not multiplied.
  const height = usesDim(shape.measure) ? (Number(shape.height_m) || 0) : null;
  const width2 = usesDim2(shape.measure) ? (Number(shape.width2_m) || 0) : null;
  const multiplier = usesDim2(shape.measure) ? (height || 0) + (width2 || 0) : (height || 0);
  const measured = round1(isLengthTimes(shape.measure) ? shape.raw_value * multiplier : shape.raw_value);
  const rate = round2(surface?.rate ?? shape.rate ?? 0);
  const factor = factors[shape.soiling] ?? 1;
  const unit_price = round2(rate * factor * (coats || 1)); // the qty stays the area; coats go into the price
  return {
    unit: info.unit,
    height_m: height,
    width2_m: width2,
    measured_value: measured,
    rate,
    coats,
    soiling_factor: factor,
    unit_price,
    line_total: round2(measured * unit_price)
  };
}

/** Customer-facing line description: what, how dirty, how much. */
export function describeShape(shape, surface, priced) {
  const surfaceName = surface?.name || shape.surface_name || 'Surface';
  const label = String(shape.label || '').trim();
  const head = label && label.toLowerCase() !== surfaceName.toLowerCase() ? `${label} — ${surfaceName}` : surfaceName;
  const soil = factorText(shape.soiling, priced.coats);
  let size;
  if (usesDim2(shape.measure)) size = `${fmtMeasure(shape.raw_value, 'lm')} × (${fmtDim(priced.height_m || 0)} + ${fmtDim(priced.width2_m || 0)}) m = ${fmtMeasure(priced.measured_value, 'm2')}`;
  else if (isLengthTimes(shape.measure)) size = `${fmtMeasure(shape.raw_value, 'lm')} × ${fmtDim(priced.height_m || 0)} m = ${fmtMeasure(priced.measured_value, 'm2')}`;
  else if (shape.measure === 'ring' && shape.estimated) size = `≈ ${fmtMeasure(shape.perimeter || 0, 'lm')} × ${fmtDim(priced.height_m || 0)} m = ${fmtMeasure(priced.measured_value, 'm2')}`;
  else size = fmtMeasure(priced.measured_value, priced.unit);
  return `${head}${soil} · ${size}${priced.coats ? ` · ${coatsText(priced.coats)}` : ''}`;
}

/**
 * Turn the estimator's shapes into quote lines + persisted measurements.
 * Returns { items, measurements, subtotal, topUp }.
 */
export function buildQuoteLines(shapes, surfaces, settings, { groupBySurface = false, priceLines = true } = {}) {
  const factors = soilingFactors(settings);
  const byId = Object.fromEntries((surfaces || []).map((s) => [String(s.id), s]));
  const measurements = [];
  const rows = [];

  shapes.forEach((shape, i) => {
    if (shape.role === 'exclude') {
      // A cut-out is remembered (so the map reloads it) but never priced.
      measurements.push({
        surface_rate_id: null, label: shape.label || 'Cut-out', kind: 'polygon', geojson: shape.geojson,
        measure: 'exclude', raw_value: round1(shape.raw_value), measured_value: 0, height_m: null,
        soiling: 'light', soiling_factor: 1, rate: 0, sort_order: i
      });
      return;
    }
    const surface = byId[String(shape.surface_rate_id)] || null;
    const priced = priceShape(shape, surface, factors);
    measurements.push({
      surface_rate_id: shape.surface_rate_id || null,
      label: shape.label || surface?.name || (shape.kind === 'line' ? 'Run' : 'Area'),
      kind: shape.kind,
      geojson: shape.geojson,
      measure: shape.measure,
      raw_value: round1(shape.raw_value),
      measured_value: priced.measured_value,
      height_m: priced.height_m,
      dim2_m: priced.width2_m,
      soiling: shape.soiling || 'light',
      soiling_factor: priced.soiling_factor,
      coats: priced.coats,
      rate: priced.rate,
      sort_order: i
    });
    rows.push({ shape, surface, priced });
  });

  let items;
  if (groupBySurface) {
    const groups = new Map();
    for (const r of rows) {
      const key = [r.shape.surface_rate_id, r.shape.soiling, r.priced.coats ?? '', r.priced.height_m ?? '', r.priced.width2_m ?? ''].join('|');
      const g = groups.get(key) || { rows: [] };
      g.rows.push(r);
      groups.set(key, g);
    }
    items = [...groups.values()].map(({ rows: grp }) => {
      const { surface, priced, shape } = grp[0];
      const measured = round1(grp.reduce((s, r) => s + r.priced.measured_value, 0));
      const surfaceName = surface?.name || 'Surface';
      const soil = factorText(shape.soiling, priced.coats);
      const count = grp.length > 1 ? ` · ${grp.length} areas` : '';
      return {
        description: `${surfaceName}${soil}${count} · ${fmtMeasure(measured, priced.unit)}${priced.coats ? ` · ${coatsText(priced.coats)}` : ''}`,
        qty: measured, unit_price: priced.unit_price, unit: priced.unit, source: 'map'
      };
    });
  } else {
    items = rows.map(({ shape, surface, priced }) => ({
      description: describeShape(shape, surface, priced),
      qty: priced.measured_value, unit_price: priced.unit_price, unit: priced.unit, source: 'map'
    }));
  }

  const subtotal = round2(items.reduce((s, it) => s + round2(it.qty * it.unit_price), 0));
  const minCharge = Number(settings.min_charge) || 0;
  const topUp = minCharge > 0 && subtotal > 0 && subtotal < minCharge ? round2(minCharge - subtotal) : 0;
  if (topUp) items.push({ description: 'Minimum charge', qty: 1, unit_price: topUp, unit: null, source: 'map' });

  // "Areas only": remember the shapes (for the areas list / map on the quote)
  // but put no priced lines on it — Andrew quotes a lump sum by hand.
  if (!priceLines) return { items: [], measurements, subtotal: 0, topUp: 0, minCharge, priced: false, grouped: false };
  return { items, measurements, subtotal, topUp, minCharge, priced: true, grouped: !!groupBySurface };
}

/**
 * Saved measurements → the shape records buildQuoteLines() expects, so a quote
 * can rebuild its priced lines (per area / per surface / none) without the map.
 */
export function measurementsToShapes(measurements, surfaces = []) {
  const byId = Object.fromEntries((surfaces || []).map((s) => [String(s.id), s]));
  return (measurements || []).map((m) => {
    const isCut = m.measure === 'exclude';
    const surface = byId[String(m.surface_rate_id)];
    return {
      role: isCut ? 'exclude' : 'surface',
      surface_rate_id: m.surface_rate_id || null,
      surface_name: surface ? undefined : m.label,
      label: m.label,
      kind: m.kind,
      geojson: m.geojson,
      measure: isCut ? 'exclude' : (surface?.measure || m.measure),
      raw_value: Number(m.raw_value) || 0,
      height_m: m.height_m ?? null,
      width2_m: m.dim2_m ?? null,
      soiling: m.soiling || 'light',
      coats: m.coats ?? null,
      rate: m.rate
    };
  });
}
